import axios from "axios";

import { apiCache, type CacheTypename } from "@/api/apiCache";
import { firebaseAuth } from "@/utils/firebaseAuth";
import { createLogger } from "@/utils/logger";
import { getMobileBridgeIdToken, isInsideMobileWebView } from "@/utils/mobileBridgeAuth";

import type { Error as ApiError } from "@/api/api.generated.schemas";
import type { AxiosError, AxiosRequestConfig } from "axios";

const isClient = typeof window !== "undefined";

const logger = createLogger("api-client");

export const baseURL = process.env.CORCA_API_URL || process.env.NEXT_PUBLIC_CORCA_API_URL || "";

export const axiosInstance = axios.create({
  baseURL,
});

export async function getAuthToken(forceRefresh = false): Promise<string | null> {
  if (isInsideMobileWebView()) {
    // In the mobile WebView the Firebase web SDK is unauthenticated; ask the
    // native bridge for a fresh ID token instead. The native side refreshes
    // automatically via @react-native-firebase, so forceRefresh is a no-op.
    return getMobileBridgeIdToken();
  }
  if (firebaseAuth.currentUser) {
    return firebaseAuth.currentUser.getIdToken(forceRefresh);
  }
  return null;
}

if (isClient) {
  axiosInstance.interceptors.request.use(async (config) => {
    const token = await getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      // Parse attached filename from content-disposition header
      // see https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Content-Disposition
      if (response.data instanceof Blob && response.headers["content-disposition"]) {
        const contentDisposition = response.headers["content-disposition"] as string;
        const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          response.data = new File([response.data], filenameMatch[1]);
        }
      }

      if (response.headers["x-meta"]) {
        try {
          const meta = JSON.parse(
            new TextDecoder().decode(Uint8Array.fromBase64(response.headers["x-meta"] as string)),
          );
          console.warn("Response meta:", meta);
        } catch {
          // ignore invalid meta
        }
      }
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & { isRetry?: boolean };
      if (error.response?.status === 401 && originalRequest && !originalRequest.isRetry) {
        originalRequest.isRetry = true;
        const newToken = await getAuthToken(true);
        if (newToken) {
          return axiosInstance(originalRequest);
        }
      }
      return Promise.reject(error);
    },
  );
}

export function isApiError(err: unknown): err is AxiosError<ApiError> {
  return axios.isAxiosError(err);
}

export function getApiErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    return err.response?.data.message ?? err.message;
  }
  if (err instanceof Error) {
    return err.message;
  }
  if (err && typeof err === "object" && "message" in err && typeof err.message === "string") {
    return err.message;
  }
  return "Unknown error";
}

async function callClient<T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig & { skipCache?: boolean },
): Promise<T | Record<string, never>> {
  const { skipCache, ...axiosOptions } = options || {};

  try {
    const { data } = await axiosInstance<T>({
      ...config,
      ...axiosOptions,
    });
    if (isClient && !skipCache) {
      return apiCache.set(data);
    }
    return data;
  } catch (error) {
    if (axios.isCancel(error)) {
      // @ts-expect-error types for Cancel are incomplete
      const reason: unknown = error.config?.signal?.reason;
      const message = reason instanceof Error ? reason.message : String(reason);
      logger.debug("Request canceled:", message);
      return {};
    }
    throw error;
  }
}

export async function client<T>(
  config: AxiosRequestConfig,
  options: AxiosRequestConfig & {
    skipCache?: boolean;
    /** TODO: documentation */
    optimistic?: {
      data: T;
      onError?: (error: unknown, data: T) => void;
      onSuccess?: (data: T) => void;
    };
  } = {},
): Promise<T | Record<string, never>> {
  const { optimistic, ...clientOptions } = options;

  if (!optimistic) {
    return callClient(config, clientOptions);
  }

  const { data, onError, onSuccess } = optimistic;
  const optimisticObject = findObjectWithUid(data);
  const optimisticUid = optimisticObject?.uid;
  const optimisticTypename = optimisticObject?.typename;

  const originalObject =
    optimisticUid && optimisticTypename
      ? apiCache.get(optimisticTypename, optimisticUid)
      : undefined;
  const cachedResult = apiCache.set(data);

  // Make API call in background
  callClient<T>(config, clientOptions)
    .then((result) => {
      const realObject = findObjectWithUid(result);
      if (optimisticUid && realObject && realObject.uid !== optimisticUid) {
        apiCache.updateKey(optimisticUid, realObject.uid);
      }
      apiCache.set(result);
      onSuccess?.(result as T);
    })
    .catch((error) => {
      if (onError) {
        onError(error, data);
      } else if (optimisticUid && optimisticTypename) {
        if (originalObject) {
          // TODO: recursively rollback optimistic objects
          apiCache.set(originalObject);
        } else {
          // TODO: recursively remove optimistic objects
          apiCache.delete(optimisticTypename, optimisticUid);
        }
      }
      // TODO: sent to sentry
      console.error("Optimistic update failed:", error);
    });

  // Return optimistic result immediately
  return cachedResult;
}

/**
 * Find the first object with uid and typename properties
 */
function findObjectWithUid(data: unknown): { uid: string; typename: CacheTypename } | null {
  if (typeof data !== "object" || data === null) {
    return null;
  }

  if (
    "uid" in data &&
    typeof data.uid === "string" &&
    "typename" in data &&
    typeof data.typename === "string"
  ) {
    return data as { uid: string; typename: CacheTypename };
  }

  for (const value of Object.values(data)) {
    const found = findObjectWithUid(value);
    if (found) {
      return found;
    }
  }

  return null;
}
